/* eslint-disable max-nested-callbacks */
import React, { Fragment, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as R from 'ramda';
import Polygon from '@shared/polygon';
import TerraDrawReactGoogleMapsDrawingManager from '@shared/drawing';
import {
  disableDrawingManager,
  enableDrawingManager,
  fetchEntitiesFromPolygon,
  setGroupApplyShape,
  setGroupLoading,
  setGroupShape
} from '@actions/groups-actions';
import { fitBounds } from '@actions/map-actions';
import EntityMapFigure from '@components/map/components/entities/map-figure';
import { mapConfig } from '@constants/component-configs';
import {
  optimizeEntitiesForMapSelector
} from '@selectors/groups-selector';
import { getGroupEntitiesList } from '@utils/gantt-utils';
import { getFiltersFromGoogleMapsShape, convertFromGoogleMapsShape } from '@utils/map-tools-utils';
import { generatePolygonPaths } from '@utils/map-utils';
import MarkerOverlaps from './marker-overlaps';
import BoundaryEntities from './boundary-entities';

const Components = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const polygonRef = useRef(null);
  const prevLocationRef = useRef(location);
  const prevApplyShapeRef = useRef(false);

  // Selectors
  const applyShapeProp = useSelector(state => state.groups.edit.applyShape);
  const drawing = useSelector(state => state.groups.edit.drawing);
  const entities = useSelector(optimizeEntitiesForMapSelector);
  const groupId = useSelector(state => state.groups.edit.group.id || null);
  const groupType = useSelector(state => state.groups.edit.group.type);
  const shape = useSelector(state => R.path(['auto_area', 'shape'], state.groups.edit.group));
  const startDate = useSelector(state => R.path(['auto_area', 'start_date'], state.groups.edit.group));
  const endDate = useSelector(state => R.path(['auto_area', 'end_date'], state.groups.edit.group));

  const getEntitiesToFetch = useCallback(() => {
    // Return only entities allowed for this group type:
    return getGroupEntitiesList(groupType);
  }, [groupType]);

  // Removes the shape from the map (only for DrawingManager-created shapes):
  const removeShape = useCallback(mapsShape => {
    // This will run only for when we delete a shape created with the
    // DrawingManager class. The DrawingManager creates shapes that are
    // not managed by React, so we need to manually remove them.
    if (mapsShape && mapsShape.setMap) {
      mapsShape.setMap(null);
    }
  }, []);

  // Applies the edited shape into the store:
  // The `isFromDrawingManager` flag distinguishes between:
  // - DrawingManager shapes: need to be removed after extracting data (they're not React-managed)
  // - Polygon component shapes: managed by React, should NOT be removed
  const applyShape = useCallback((mapsShape, isFromDrawingManager = false) => {
    let geomIn = null;
    let shapeData = null;

    if (mapsShape && R.hasIn('getPath', mapsShape)) {
      // Read shape info from Google Maps Polygon instance
      geomIn = getFiltersFromGoogleMapsShape(mapsShape);
      shapeData = convertFromGoogleMapsShape(mapsShape);
      // Only remove if it came from DrawingManager (not React-managed)
      if (isFromDrawingManager) {
        removeShape(mapsShape);
      }
    } else if (shape) {
      // Read shape from existing group shape:
      const { coordinates, type } = shape;
      if (type === 'Polygon') {
        geomIn = `POLYGON((${coordinates[0].map(coordinate => coordinate.join(' ')).join(',')}))`;
      } else if (type === 'MultiPolygon') {
        const polygons = coordinates.map(polygonCoords =>
          `(${polygonCoords[0].map(coordinate => coordinate.join(' ')).join(',')})`
        );
        geomIn = `MULTIPOLYGON(${polygons.join(',')})`;
      }
      shapeData = shape;
    } else {
      // No shape set yet:
      return;
    }

    dispatch(setGroupLoading(true)).then(() => {
      dispatch(setGroupShape(shapeData)).then(() => {
        const fetchParams = {
          geom_in: geomIn,
          generates_overlaps: 1,
          ...(startDate && endDate && {
            // It's correct to apply endDate to start_date__lte, there's no mistake,
            // since it's lte (less than equal), the same for end_date__gte:
            start_date__lte: endDate,
            end_date__gte: startDate
          }),
          ...(groupId && shape && { unknown_to_group: groupId })
        };
        const promises = [
          ...getEntitiesToFetch().map(entity => dispatch(fetchEntitiesFromPolygon(fetchParams, entity))),
          dispatch(disableDrawingManager())
        ];
        Promise.all(promises).then(() => dispatch(setGroupLoading(false)));
      });
    });
  }, [dispatch, shape, startDate, endDate, groupId, getEntitiesToFetch, removeShape]);

  const initializePage = useCallback(() => {
    const locationShape = R.path(['state', 'shape'], location);
    if (locationShape) {
      dispatch(fitBounds([{ shape: locationShape }]));
      // Set the shape directly in Redux from the GeoJSON location state,
      // then trigger applyShape via the applyShape prop mechanism.
      // This avoids the Google Maps round-trip (convertToGoogleMapsShape →
      // convertFromGoogleMapsShape) which can fail silently for shapes
      // that originate from layers rather than the drawing tool.
      dispatch(setGroupShape(locationShape));
      dispatch(enableDrawingManager());
      dispatch(setGroupApplyShape());
    }
  }, [dispatch, location]);

  // Called when we complete a polygon (i.e. when we close the shape)
  // on the DrawingManager.
  const onPolygonComplete = useCallback(mapsShape => {
    // DrawingManager shapes need to be removed after extraction
    applyShape(mapsShape, true);
  }, [applyShape]);

  // Initial mount
  useEffect(() => {
    initializePage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for applyShape prop change
  useEffect(() => {
    if (!prevApplyShapeRef.current && applyShapeProp) {
      applyShape(polygonRef.current);
    }
    prevApplyShapeRef.current = applyShapeProp;
  }, [applyShapeProp, applyShape]);

  // Watch for location changes (equivalent to componentDidUpdate)
  useEffect(() => {
    if (location !== prevLocationRef.current) {
      initializePage();
    }
    prevLocationRef.current = location;
  }, [location, initializePage]);

  // Memoize paths to prevent unnecessary resets when user is editing.
  // Polygon resets its path whenever the `paths` prop changes, which would reset
  // user's edits. By memoizing, paths only changes when shape changes.
  const polygonPaths = useMemo(() => {
    if (shape && shape.type === 'Polygon' && shape.coordinates) {
      return generatePolygonPaths(shape.coordinates);
    }
    return null;
  }, [shape]);

  const multiPolygonPaths = useMemo(() => {
    if (shape && shape.type === 'MultiPolygon' && shape.coordinates) {
      return shape.coordinates.map(coords => generatePolygonPaths(coords));
    }
    return null;
  }, [shape]);

  // Render the shape as a Polygon component if a shape exists.
  const renderPolygon = () => {
    // If we are in drawing mode and there's no shape,
    // render the DrawingManager component.
    if (drawing && !shape) {
      return (
        <TerraDrawReactGoogleMapsDrawingManager
          drawingMode="polygon"
          onPolygonComplete={onPolygonComplete}
          options={mapConfig.drawingManager}
        />
      );
    }

    // Else, if there's a shape render it:
    if (shape && shape.coordinates) {
      let editable = drawing;

      if (shape.type === 'MultiPolygon') {
        // MultiPolygon shapes are not editable
        editable = false;
      }

      if (shape.type === 'Polygon' && polygonPaths) {
        return (
          <Fragment>
            <Polygon
              ref={polygonRef}
              editable={editable}
              paths={polygonPaths}
            />
            <BoundaryEntities />
          </Fragment>
        );
      } else if (shape.type === 'MultiPolygon' && multiPolygonPaths) {
        return (
          <Fragment>
            {multiPolygonPaths.map((paths, index) => (
              <Polygon
                key={`polygon-${index}`}
                editable={editable}
                paths={paths}
              />
            ))}
            <BoundaryEntities />
          </Fragment>
        );
      }
    }

    return null;
  };

  return (
    <Fragment>
      {renderPolygon()}
      <MarkerOverlaps />
      {entities && entities.map(entity => (
        <EntityMapFigure
          key={entity.id}
          {...entity}
          markerSource="group"
          type={entity.entityType}
        />
      ))}
    </Fragment>
  );
};

export default memo(Components);

